import { Slot, Slottable } from '@radix-ui/react-slot';
import { AlertTriangle, CheckCircle, XClose } from '@timelyapp/icons/solid-icons';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import React from 'react';
import { CustomIcon } from '../icons/CustomIcon';
import { Text } from '../text/Text';
import styles from './ Message.module.scss';

const cvaMessage = cva(styles.messageContainer, {
  variants: {
    variant: {
      neutral: styles['variant-neutral'],
      info: styles['variant-info'],
      alert: styles['variant-alert'],
      danger: styles['variant-danger'],
      success: styles['variant-success'],
      warning: styles['variant-warning'],
    },
    size: {
      small: styles['size-small'],
      medium: styles['size-medium'],
    },
    autoIcon: {
      true: styles['auto-icon'],
    },
  },
  defaultVariants: { variant: 'neutral', size: 'medium' },
});

export const autoIconMap = {
  neutral: <CustomIcon.Info />,
  info: <CustomIcon.Info />,
  alert: <AlertTriangle />,
  danger: <AlertTriangle />,
  success: <CheckCircle />,
  warning: <AlertTriangle />,
} as const;

export type MessageProps = VariantProps<typeof cvaMessage> & {
  asChild?: boolean;
  title?: string;
  /** Should an icon be set automatically based on variant? Not applicable to all variants. */
  autoIcon?: boolean;
  icon?: React.ReactNode; // todo: 'auto', handle as autoIcon. deprecate autoIcon.
  /** If provided, a dismiss icon will be available */
  onDismiss?: () => void;
};

type Props = React.HTMLAttributes<HTMLDivElement> & MessageProps;
export const Message = React.forwardRef<HTMLDivElement, Props>((props, ref) => {
  const {
    asChild,
    onDismiss,
    className,
    title,
    icon,
    autoIcon,
    variant = 'neutral',
    size,
    ...rest
  } = props;
  const Component = asChild ? Slot : 'div';
  const finalIcon = React.isValidElement(icon)
    ? icon
    : autoIcon && variant && autoIconMap[variant]
      ? autoIconMap[variant]
      : null;

  return (
    <Component ref={ref} {...rest} className={cvaMessage({ variant, size, autoIcon, className })}>
      {!!onDismiss && (
        <button className={styles.dismissButton} onClick={onDismiss} aria-label="Dismiss">
          <XClose />
        </button>
      )}
      {finalIcon && React.cloneElement(finalIcon as React.ReactElement, { className: styles.icon })}
      {!!title && <Text.Label>{title}</Text.Label>}

      <Slottable>{props.children}</Slottable>
    </Component>
  );
});
